import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

/**
 * The Library tab: the read-aloud passages as entries to browse, search and start from.
 * Shared by web and mobile; the screens draw it.
 */
export interface LibraryEntry {
  lineageUuid: string;
  /** The version shown, when known: what Saved points at. */
  uuid?: string;
  /** Two sentences about the book (Library › book info). */
  summary?: string;
  /** "A Bank Fraud". */
  title: string;
  /** The author. */
  byline?: string;
  /** The row's tag: the book's category (the library), or its kind from the title (workbook). */
  tag?: string;
  /** Already read in an earlier exercise. */
  used: boolean;
  /** Category keys of the library (`LIBRARY_CATEGORY_LABEL`). */
  categories?: string[];
  /** Estimated reading time, whole minutes. */
  minutes?: number;
  /** Cover urls: `card` 198 × 141 (cards), `row` 64 × 123 (rows), on `background` while loading. */
  cover?: { card?: string; row?: string; background?: string };
}

/**
 * The read-aloud library's categories (scripts/seeds/read-aloud), translated, in the order the
 * design shows them (Figma library). A category the library adds later shows under the
 * library's own English name, after these, until it is added here.
 */
export const LIBRARY_CATEGORY_LABEL: Record<string, MessageDescriptor> = {
  'friendship-kindness': msg`Friendship & Kindness`,
  'play-imagination': msg`Play & Imagination`,
  'home-belonging': msg`Home & Belonging`,
  'plants-trees': msg`Plants & Trees`,
  'birds-animals': msg`Birds & Animals`,
  'seasons-weather': msg`Seasons & Weather`,
  'rivers-ponds-seas': msg`Rivers, Ponds & Seas`,
  'exploration-discovery': msg`Exploration & Discovery`,
};

const CATEGORY_ORDER = Object.keys(LIBRARY_CATEGORY_LABEL);

/** Categories in the design's order; ones it doesn't know keep the library's order, after. */
export function inDesignOrder<T extends { key: string }>(categories: readonly T[]): T[] {
  const rank = (key: string) => {
    const index = CATEGORY_ORDER.indexOf(key);
    return index === -1 ? CATEGORY_ORDER.length : index;
  };
  return [...categories].sort((x, y) => rank(x.key) - rank(y.key));
}

/** The entries in one category, in the library's order. */
export function inCategory(entries: readonly LibraryEntry[], key: string): LibraryEntry[] {
  return entries.filter((entry) => entry.categories?.includes(key));
}

/**
 * A passage title split into what it carries. The library stores one line per passage —
 * "A Bank Fraud [Short story], by Rudyard Kipling" — so the cards can show the title and the
 * author apart, as the design does, and the kind as a tag. Anything that doesn't follow the
 * pattern is all title.
 */
export function passageCredit(raw: string): { title: string; author?: string; kind?: string } {
  const match = /^(.*?)(?:\s*\[([^\]]+)\])?(?:,\s*by\s+(.+))?$/.exec(raw.trim());
  const title = match?.[1]?.trim();
  if (!match || !title) return { title: raw.trim() };
  const kind = match[2]?.trim();
  const author = match[3]?.trim();
  return { title, ...(author ? { author } : {}), ...(kind ? { kind } : {}) };
}

/**
 * "For you": what hasn't been used yet, in the library's own order. Once everything has been
 * used, everything shows again.
 */
export function forYou(entries: readonly LibraryEntry[]): LibraryEntry[] {
  const fresh = entries.filter((entry) => !entry.used);
  return fresh.length > 0 ? fresh : [...entries];
}

/**
 * Reading's "Pick for me": one of the "For you" books at random, so each pick can be a
 * different one. `random` returns [0, 1), as `Math.random` does.
 */
export function pickForMe(
  entries: readonly LibraryEntry[],
  random: () => number = Math.random,
): LibraryEntry | undefined {
  const pool = forYou(entries);
  return pool[Math.floor(random() * pool.length)];
}

/** Lowercase, without accents: "Café" finds "cafe" and the other way round. */
function folded(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * The entries whose title, byline or tags contain every word of `query`; all of them for an
 * empty one.
 */
export function searchLibrary<
  T extends { title: string; byline?: string; tags?: readonly string[] },
>(entries: readonly T[], query: string): T[] {
  const words = folded(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...entries];
  return entries.filter((entry) => {
    const haystack = folded(`${entry.title} ${entry.byline ?? ''} ${(entry.tags ?? []).join(' ')}`);
    return words.every((word) => haystack.includes(word));
  });
}

export const libraryShelfSchema = z.enum(['reading', 'math']);
export type LibraryShelf = z.infer<typeof libraryShelfSchema>;

/**
 * The Library's route params, on web and mobile. `shelf` is the shelf it opens on; `from` is the
 * exercise whose "Choose from Library" opened it, which keeps the Library to that exercise's
 * shelf. Anything unreadable is dropped.
 */
export const libraryParamsSchema = z.object({
  shelf: libraryShelfSchema.optional().catch(undefined),
  from: libraryShelfSchema.optional().catch(undefined),
});
export type LibraryParams = z.infer<typeof libraryParamsSchema>;

/**
 * The shelf the Library shows and whether it offers the other: both shelves as a destination of
 * its own, only the exercise's shelf when an exercise opened it, and Reading alone where the
 * environment has no Math library.
 */
export function libraryShelves(
  { shelf, from }: LibraryParams,
  mathAvailable: boolean,
): { shelf: LibraryShelf; switchable: boolean } {
  if (!mathAvailable) return { shelf: 'reading', switchable: false };
  if (from) return { shelf: from, switchable: false };
  return { shelf: shelf ?? 'reading', switchable: true };
}
